import { Injectable } from '@angular/core';
import { PhoneNumbersService as ApiPhoneNumbersService } from '../api/services/phone-numbers.service';
import { SavePhoneNumbersRequest } from '../api/models/save-phone-numbers-request';
import { PhoneNumberModel } from '../api/models/phone-number-model';
import { LookupModel } from '../api/models/lookup-model';
import { LookupService } from '../api/services/lookup.service';
import * as _ from 'lodash';
import { PhoneNumbersExportRequest } from '../api/models/phone-numbers-export-request';
import { s2ab } from '../core/utilities/utils';
import { SmsAvailablePhoneNumbersRequest } from '../api/models/sms-available-phone-numbers-request';
import { SmsAvailablePhoneNumberModel } from '../api/models/sms-available-phone-number-model';
import { EnableSmsPhoneNumberRequest } from '../api/models/enable-sms-phone-number-request';
import { SmsProcessingPhoneNumbersRequest } from '../api/models/sms-processing-phone-numbers-request';
import { SmsProcessingPhoneNumberModel } from '../api/models/sms-processing-phone-number-model';
import { lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PhoneNumbersService {
  constructor(
    private lookupService: LookupService,
    private apiPhoneNumbersService: ApiPhoneNumbersService,
  ) {}

  exportColumns: any[] = [
    { id: _.uniqueId('id_'), field: 'number', title: 'Phone Number', width: 20 },
    { id: _.uniqueId('id_'), field: 'customerDidAlias', title: 'Phone Number Alias', width: 20 },
    { id: _.uniqueId('id_'), field: 'phoneNumberStatusName', title: 'Voice Status', width: 10 },
    { id: _.uniqueId('id_'), field: 'productTypeName', title: 'Phone Number Type', width: 10 },
    { id: _.uniqueId('id_'), field: 'campaignNumber', title: 'Campaign #', width: 10 },
    { id: _.uniqueId('id_'), field: 'cnam', title: 'Caller ID', width: 10 },
    { id: _.uniqueId('id_'), field: 'feature', title: 'Additional Features', width: 20 },
    { id: _.uniqueId('id_'), field: 'allUsers', title: 'Users Assigned To Phone Number', width: 20 },
    { id: _.uniqueId('id_'), field: 'faxInboundEmails', title: 'Fax Inbound Emails', width: 20 },
    { id: _.uniqueId('id_'), field: 'faxOutboundEmails', title: 'Fax Outbound Emails', width: 20 },
  ];

  async getPhoneNumberById(id: string): Promise<PhoneNumberModel> {
    return await lastValueFrom(this.apiPhoneNumbersService.phoneNumbersGetPhoneNumberById({ id: id }));
  }

  async savePhoneNumbers(model: SavePhoneNumbersRequest): Promise<boolean> {
    await lastValueFrom(this.apiPhoneNumbersService.phoneNumbersSavePhoneNumbers({ body: model }));

    return true;
  }

  async customerSmsUsersLookup(state?: any): Promise<[LookupModel[], number]> {
    const response = await lastValueFrom(this.lookupService.lookupGetCustomerUsers({ onlySms: true }));

    return [response.data, response.total];
  }

  async customerFaxUsersLookup(): Promise<[LookupModel[], number]> {
    const response = await lastValueFrom(this.lookupService.lookupGetCustomerUsers({ onlyFax: true }));

    return [response.data, response.total];
  }

  async customerFaxDomainsLookup(): Promise<[LookupModel[], number]> {
    const response = await lastValueFrom(this.lookupService.lookupGetCustomerUserEmailDomains());

    return [response.data, response.total];
  }

  async getSmsAvailablePhoneNumbers(req?: SmsAvailablePhoneNumbersRequest): Promise<SmsAvailablePhoneNumberModel[]> {
    const response = await lastValueFrom(
      this.apiPhoneNumbersService.phoneNumbersGetSmsAvailablePhoneNumbers({ body: req }),
    );

    return response.numbers;
  }

  async getSmsProcessingPhoneNumbers(req?: SmsProcessingPhoneNumbersRequest): Promise<SmsProcessingPhoneNumberModel[]> {
    const response = await this.apiPhoneNumbersService
      .phoneNumbersGetSmsProcessingPhoneNumbers({ body: req })
      .toPromise();

    return response.numbers;
  }

  async enableSmsNumberForUser(model: EnableSmsPhoneNumberRequest): Promise<boolean> {
    await this.apiPhoneNumbersService.phoneNumbersEnableSmsNumberForUser({ body: model }).toPromise();

    return true;
  }

  async exportPhoneNumbers(data: PhoneNumbersExportRequest): Promise<void> {
    const body = await this.apiPhoneNumbersService.phoneNumbersExportExportPhoneNumbers({ body: data }).toPromise();
    const blob = new Blob([s2ab(atob(body))], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.style.display = 'none';
    document.body.appendChild(a);

    a.href = url;
    a.download = 'phone-numbers.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);

    return;
  }
}
